import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Typ för varje objekt i varukorgen
export interface CartItem {
  uniqueId: string;
  name: string;
  selectedSize?: string;
  selectedColor?: string;
  quantity: number;
  price?: number;
  external_id: string;
  [key: string]: any; // för extra fält
}

// Typ för CartContext
interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  addToCart: (item: Partial<CartItem>) => void;
  removeFromCart: (uniqueId: string) => void;
  updateQuantity: (uniqueId: string, change: number) => void;
  updateCartItem: (uniqueId: string, updatedFields: Partial<CartItem>) => void;
  clearCart: () => void;
}

// Skapa context med typ
const CartContext = createContext<CartContextType | undefined>(undefined);

// Custom hook för att använda CartContext
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

// Props för Provider
interface CartProviderProps {
  children: ReactNode;
}

// CartProvider
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Läs in varukorgen från localStorage
  const loadCartFromStorage = (): CartItem[] => {
    const savedCart = localStorage.getItem('cartItems');
    return savedCart ? JSON.parse(savedCart) : [];
  };

  const [cartItems, setCartItems] = useState<CartItem[]>(loadCartFromStorage);

  // Spara varukorg i localStorage när cartItems ändras
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Räkna total antal produkter
  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  // Skapa uniqueId baserat på external_id + färg
  const createUniqueId = (variant: Partial<CartItem>) => {
    return `${variant.external_id || 'noid'}--${variant.selectedColor || ''}`;
  };

  // Lägg till vara i varukorgen
  const addToCart = (selectedVariant: Partial<CartItem>) => {
    const uniqueId = createUniqueId(selectedVariant);

    const itemWithUniqueId: CartItem = {
      ...selectedVariant,
      uniqueId,
      quantity: selectedVariant.quantity || 1,
      external_id: selectedVariant.external_id || 'saknas',
      name: selectedVariant.name || 'Produkt',
    } as CartItem;

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.uniqueId === uniqueId);
      if (existingIndex >= 0) {
        // Om produkten redan finns, öka kvantiteten
        return prev.map((item, index) =>
          index === existingIndex ? { ...item, quantity: item.quantity + itemWithUniqueId.quantity } : item
        );
      } else {
        return [...prev, itemWithUniqueId];
      }
    });
  };

  // Uppdatera kvantitet
  const updateQuantity = (uniqueId: string, change: number) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.uniqueId === uniqueId ? { ...item, quantity: Math.max(1, item.quantity + change) } : item
      )
    );
  };

  // Uppdatera valfri egenskap
  const updateCartItem = (uniqueId: string, updatedFields: Partial<CartItem>) => {
    setCartItems((prev) =>
      prev.map((item) => (item.uniqueId === uniqueId ? { ...item, ...updatedFields } : item))
    );
  };

  // Ta bort produkt
  const removeFromCart = (uniqueId: string) => {
    setCartItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  // Töm varukorgen
  const clearCart = () => {
    setCartItems([]);
    localStorage.setItem('cartItems', JSON.stringify([]));
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateCartItem,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
